/** Plain-English copy helpers for the sequence UI (§9.7).
 *  User-facing words are touches / steps / sequences — never enrollments, instances, or states.
 *  Counts only, never percentages (AC 18).
 */
import type {
  EnrollBlockReason,
  InstanceState,
  SequenceChannel,
  SequenceEnrollment,
  SequenceStep,
  SequenceStepInstance,
  StoppedReason,
} from '../../types/sequence';

export function channelLabel(channel: SequenceChannel): string {
  if (channel === 'email') return 'Email';
  if (channel === 'call') return 'Call';
  return 'LinkedIn';
}

const DATE_FMT: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' };

export function formatTouchDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, DATE_FMT);
}

/** "5 touches over 16 business days." — the editor's live footer. */
export function planEnglish(steps: Pick<SequenceStep, 'delay_business_days'>[]): string {
  const touches = steps.length;
  const span = steps.reduce((sum, s) => sum + (s.delay_business_days || 0), 0);
  const touchWord = touches === 1 ? 'touch' : 'touches';
  const dayWord = span === 1 ? 'business day' : 'business days';
  return `${touches} ${touchWord} over ${span} ${dayWord}.`;
}

export function stoppedReasonLabel(reason: StoppedReason): string {
  const map: Record<string, string> = {
    replied: 'replied',
    inbound_reply: 'replied',
    do_not_contact: 'do-not-contact',
    opted_out: 'opted out',
    stage_advanced: 'became a client',
    manual_stop: 'stopped by you',
    bounced: 'email bounced',
  };
  return map[reason] || 'stopped';
}

/** The state chip: "Active · Step 2 of 5" / "Stopped — replied" / "Completed" / "Paused". */
export function stateChip(enrollment: SequenceEnrollment): { label: string; tone: 'active' | 'stopped' | 'done' | 'paused' } {
  const total = enrollment.step_instances?.length ?? 0;
  if (enrollment.state === 'active') {
    const step = enrollment.current_step_index + 1;
    return { label: total ? `Active · Step ${step} of ${total}` : 'Active', tone: 'active' };
  }
  if (enrollment.state === 'paused') return { label: 'Paused', tone: 'paused' };
  if (enrollment.state === 'completed') return { label: 'Completed', tone: 'done' };
  return { label: `Stopped — ${stoppedReasonLabel(enrollment.stopped_reason)}`, tone: 'stopped' };
}

/** "Next: call, Thu Sept 4." or "Deferred to Tue Sept 9 — daily send limit reached." */
export function nextTouchLine(enrollment: SequenceEnrollment): string | null {
  if (enrollment.state !== 'active') return null;
  const pending = (enrollment.step_instances ?? [])
    .filter((i) => i.state === 'pending')
    .sort((a, b) => a.scheduled_for.localeCompare(b.scheduled_for));
  const next = pending[0];
  if (!next) return null;
  const when = formatTouchDate(next.scheduled_for);
  if (next.defer_count > 0) {
    return `Deferred to ${when} — daily send limit reached.`;
  }
  return `Next: ${channelLabel(next.channel).toLowerCase()}, ${when}.`;
}

export function instanceStateLabel(state: InstanceState): string {
  const map: Record<InstanceState, string> = {
    pending: 'Scheduled',
    sent: 'Sent',
    skipped: 'Skipped',
    cancelled: 'Cancelled',
    failed: 'Failed',
  };
  return map[state];
}

/** Blocked-enrollment copy in the interface's own voice (§9.3). Never vague, never apologetic. */
export function blockReasonMessage(reason: EnrollBlockReason): string {
  const map: Record<EnrollBlockReason, string> = {
    do_not_contact: 'This contact is on your do-not-contact list.',
    no_email_on_contact: 'This contact has no email address. Start a call-first sequence instead.',
    already_enrolled: 'This contact is already in an active sequence.',
    no_mailbox_connected: 'No mailbox is connected. Connect one in Email settings to start sending.',
    sequences_paused: 'All sequences are paused. Resume them before enrolling.',
    not_found: 'This contact could not be found.',
  };
  return map[reason];
}

export function isCallFirstBlock(reason: EnrollBlockReason): boolean {
  return reason === 'no_email_on_contact';
}

export function channelMixSummary(steps: Pick<SequenceStepInstance, 'channel'>[] | Pick<SequenceStep, 'channel'>[]): string {
  const counts: Record<string, number> = {};
  steps.forEach((s) => {
    counts[s.channel] = (counts[s.channel] || 0) + 1;
  });
  const parts: string[] = [];
  (['email', 'call', 'linkedin'] as SequenceChannel[]).forEach((ch) => {
    const n = counts[ch] || 0;
    if (!n) return;
    const label = ch === 'email' ? (n === 1 ? 'email' : 'emails') : ch === 'call' ? (n === 1 ? 'call' : 'calls') : 'LinkedIn';
    parts.push(`${n} ${label}`);
  });
  return parts.join(' · ');
}
